import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyIcon, ExternalLinkIcon, LayoutTemplateIcon, LinkIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { createLanding, deleteLanding, duplicateLanding, setLandingStatus } from '../../services/landingService';
import { LANDING_TEMPLATES, LandingTemplateId } from '../../data/landingTemplates';
import { formatDate } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Tag } from '../../components/ui/Tag';
const STATUS_LABEL = {
  borrador: 'Borrador',
  publicada: 'Publicada',
  despublicada: 'Despublicada'
} as const;
export function Landings() {
  const navigate = useNavigate();
  const {
    organizationId,
    landings,
    forms,
    leads
  } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [formId, setFormId] = useState('');
  const [template, setTemplate] = useState<LandingTemplateId>('con_video');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  async function handleCreate() {
    if (!name.trim()) return;
    setSaving(true);
    const landing = await createLanding(organizationId, name.trim(), formId || undefined, template);
    setSaving(false);
    setCreating(false);
    setName('');
    setFormId('');
    setTemplate('con_video');
    navigate(`/app/landings/${landing.id}/editor`);
  }
  return <>
      <PageHeader title="Landings" description="Páginas públicas para captar potenciales clientes." actions={<Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-963" data-mp="2gc2vq76wl9y" />} onClick={() => setCreating(true)} data-id="element-962" data-mp="2fsnoh8xcazm">
            Nueva landing
          </Button>} data-id="element-961" data-mp="7fx6wf17vjtj" />

      {landings.length === 0 ? <EmptyState icon={<LayoutTemplateIcon className="h-5 w-5" data-id="element-965" data-mp="bi1y774704d0" />} title="Todavía no tienes landings" description="Crea tu primera landing y conecta un formulario para empezar a captar potenciales clientes." action={<Button onClick={() => setCreating(true)} data-id="element-966" data-mp="3ftxbp062vkvq">Crear landing</Button>} data-id="element-964" data-mp="bile6iaa8ioe" /> : <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-967" data-mp="3tp813y1jzdsp">
          {landings.map(landing => {
        const form = forms.find(f => f.id === landing.formId);
        const landingLeads = leads.filter(l => l.origin.landingId === landing.id);
        const hasVideo = landing.pageConfig.blocks.some(b => b.type === 'video' && b.visible);
        return <li key={landing.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-968" data-mp="1ggf9sq0h8w6q">
                <div className="flex items-start justify-between gap-3" data-id="element-969" data-mp="12qvaxva0oj2p">
                  <div className="min-w-0" data-id="element-970" data-mp="17owuehbcukep">
                    <Link to={`/app/landings/${landing.id}/editor`} className="block truncate text-base font-semibold text-foreground hover:text-primary" data-id="element-971" data-mp="17lr26t7fj2er">
                      {landing.name}
                    </Link>
                    <p className="mt-1 truncate text-sm text-muted-foreground" data-id="element-972" data-mp="1lhmhkp2mdvde">
                      /landing/{landing.slug}
                    </p>
                  </div>
                  <Dropdown align="right" trigger={<span className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-surface" data-id="element-974" data-mp="f09427br14ua">
                        ⋯
                      </span>} items={[{
              label: 'Editar',
              icon: <PencilIcon className="h-4 w-4" data-id="element-975" data-mp="lhudwzojm7p0" />,
              onSelect: () => navigate(`/app/landings/${landing.id}/editor`)
            }, {
              label: 'Ver landing',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-976" data-mp="1d5mfkiyq0gc4" />,
              onSelect: () => window.open(`/landing/${landing.slug}`, '_blank')
            }, {
              label: 'Copiar enlace',
              icon: <LinkIcon className="h-4 w-4" data-id="element-977" data-mp="z7eb7pzcucqx" />,
              onSelect: () => navigator.clipboard?.writeText(`${window.location.origin}/landing/${landing.slug}`)
            }, {
              label: 'Duplicar',
              icon: <CopyIcon className="h-4 w-4" data-id="element-978" data-mp="3etba6kssc8m6" />,
              onSelect: () => duplicateLanding(landing.id)
            }, {
              label: landing.status === 'publicada' ? 'Despublicar' : 'Publicar',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-979" data-mp="3sl7yedrc1yda" />,
              onSelect: () => setLandingStatus(landing.id, landing.status === 'publicada' ? 'despublicada' : 'publicada')
            }, {
              label: 'Eliminar',
              icon: <Trash2Icon className="h-4 w-4" data-id="element-980" data-mp="285nvz5dhqi6n" />,
              destructive: true,
              onSelect: () => setToDelete(landing.id)
            }]} data-id="element-973" data-mp="9zlgjvmqb64t" />
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm" data-id="element-981" data-mp="253rs0vzufvem">
                  <div data-id="element-982" data-mp="205hwgka7issh">
                    <dt className="text-xs text-muted-foreground" data-id="element-983" data-mp="2069hj166h84i">Leads</dt>
                    <dd className="font-semibold text-foreground" data-id="element-984" data-mp="2f2h05x1xu1ho">{landingLeads.length}</dd>
                  </div>
                  <div data-id="element-985" data-mp="2a11go9fes31e">
                    <dt className="text-xs text-muted-foreground" data-id="element-986" data-mp="2a4z1r0ljr4nx">Visitas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-987" data-mp="2o0kmgvkqz2aj">{landing.views}</dd>
                  </div>
                  <div data-id="element-988" data-mp="2j180se5m419j">
                    <dt className="text-xs text-muted-foreground" data-id="element-989" data-mp="2xs7357futtko">Formulario</dt>
                    <dd className="truncate text-muted-foreground" data-id="element-990" data-mp="2xu9wa0cb02tm">
                      {form?.name ?? 'Sin asociar'}
                    </dd>
                  </div>
                  <div data-id="element-991" data-mp="36tofqfu1zd4m">
                    <dt className="text-xs text-muted-foreground" data-id="element-992" data-mp="36qsm33p8nalz">Modificada</dt>
                    <dd className="text-muted-foreground" data-id="element-993" data-mp="32suvpxjqeih0">{formatDate(landing.updatedAt)}</dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4" data-id="element-994" data-mp="3qeooaq6rzws6">
                  <Tag data-id="element-995" data-mp="3qcavyp3hcyob">{STATUS_LABEL[landing.status]}</Tag>
                  <Tag data-id="element-996" data-mp="3ce47u0oc2d1u">{hasVideo ? 'Con vídeo' : 'Sin vídeo'}</Tag>
                </div>
              </li>;
      })}
        </ul>}

      <Modal open={creating} onClose={() => setCreating(false)} title="Nueva landing" footer={<div className="flex justify-end gap-2" data-id="element-998" data-mp="37g2mf93fq1n7">
            <Button variant="ghost" onClick={() => setCreating(false)} data-id="element-999" data-mp="37hnjb5d81wo8">
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!name.trim()} data-id="element-1000" data-mp="2xpznks9mttgy">
              Crear
            </Button>
          </div>} data-id="element-997" data-mp="32p2t0dtiy1pg">
        <div className="space-y-5" data-id="element-1001" data-mp="2jqqgqj7bgbpn">
          <fieldset data-id="element-1002" data-mp="2opbi4lyb11fo">
            <legend className="mb-2 text-sm font-medium text-foreground" data-id="element-1003" data-mp="2opup57mf25ex">
              Plantilla de partida
            </legend>
            <div className="grid gap-3 sm:grid-cols-2" data-id="element-1004" data-mp="3461ibxciarbh">
              {LANDING_TEMPLATES.map(option => {
              const active = template === option.id;
              return <button key={option.id} type="button" onClick={() => setTemplate(option.id)} aria-pressed={active} className={`rounded-xl border p-4 text-left transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`} data-id="element-1005" data-mp="2v5laycsn701c">
                    <span className="block text-sm font-semibold text-foreground" data-id="element-1006" data-mp="27hlm5iew5jpe">
                      {option.name}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground" data-id="element-1007" data-mp="22h7e40dai9rd">
                      {option.description}
                    </span>
                  </button>;
            })}
            </div>
          </fieldset>
          <Input id="landing-name" label="Nombre interno" placeholder="Campaña inmobiliarias octubre" value={name} onChange={e => setName(e.target.value)} data-id="element-1008" data-mp="hz5a66be4141" />
          <Select id="landing-form" label="Formulario asociado" placeholder="Selecciona un formulario" options={forms.map(f => ({
          value: f.id,
          label: f.name
        }))} value={formId} onChange={e => setFormId(e.target.value)} data-id="element-1009" data-mp="r036e2fl74c2" />
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(toDelete)} title="Eliminar landing" description="La página dejará de estar disponible públicamente. Esta acción no se puede deshacer." onCancel={() => setToDelete(null)} onConfirm={async () => {
      if (toDelete) await deleteLanding(toDelete);
      setToDelete(null);
    }} data-id="element-1010" data-mp="81qtc5lk1ab7" />
    </>;
}